import React, { useMemo, useState, useCallback, useEffect } from "react";
import CurrencyInput from "react-currency-input-field";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ButtonComp, CardComp } from "../../../components";
import { formatMoney, getMemberTransactionChargesTotal } from "../../../utils";
import moment from "moment";
import {
  useGetMemberSingleRepaymentQuery,
  usePayMemberLoanRepaymentMutation,
  usePayMemberLoanRepaymentBankTransferMutation,
} from "../../../store/member/member-loan";
import toast from "react-hot-toast";
import { BsInfoCircle } from "react-icons/bs";
import PageWrapper from "../modules/PageWrapper";
import MemberPaymentModeChooser from "../modules/MemberPaymentModeChooser";
import { Spinner } from "react-bootstrap";
import {
  installmentBalance,
  paymentEffect,
  validatePaymentAmount,
} from "../../../components/Loan/partialRepayment";
import {
  useGetMemberPaymentFeePreviewQuery,
  useGetMemberCompanyBanksQuery,
} from "../../../store/member/member-contribution";
import MemberPendingBankTransfersCard from "../modules/MemberPendingBankTransfersCard";
import {
  feePreviewChannel,
  getFrontendBaseUrl,
  isPaystackMode,
  isSocietyBankMode,
  redirectToMemberOnlinePayment,
} from "../../../utils/memberOnlinePayment";

export default function MemberLoanRepaymentPay() {
  const { repaymentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loanRequestId = searchParams.get("loan_request_id");
  const [paymentMode, setPaymentMode] = useState("paystack");
  const [selectedBankId, setSelectedBankId] = useState("");
  const [memberNote, setMemberNote] = useState("");

  const { data: repaymentData, isLoading, error } = useGetMemberSingleRepaymentQuery(
    repaymentId,
    {
      skip: !repaymentId,
    }
  );

  const [payRepayment, { isLoading: isPaying }] = usePayMemberLoanRepaymentMutation();
  const [payBankTransfer, { isLoading: isBankSubmitting }] =
    usePayMemberLoanRepaymentBankTransferMutation();
  const { data: companyBanks = [] } = useGetMemberCompanyBanksQuery();

  const handlePaymentModeChange = useCallback((mode) => {
    setPaymentMode(mode);
    if (isSocietyBankMode(mode)) {
      return;
    }
    setSelectedBankId("");
    setMemberNote("");
  }, []);

  // Extract repayment details - API returns data directly after transformResponse
  const repaymentDetails = repaymentData || null;
  
  // Part payments: the server says whether this loan allows them and what the loan still owes
  const partialEnabled = Boolean(repaymentDetails?.partial_repayment_enabled);
  const installmentLeft = useMemo(
    () => (repaymentDetails ? installmentBalance(repaymentDetails) : 0),
    [repaymentDetails]
  );
  // Late fees on the loan are paid first, so clearing this installment takes both
  const lateFeesOwed = Math.max(0, Number(repaymentDetails?.late_fee_balance) || 0);
  const balance = useMemo(() => {
    if (!repaymentDetails) return 0;
    const payable = Number(repaymentDetails.payable_amount);
    return Number.isFinite(payable) && repaymentDetails.payable_amount != null
      ? payable
      : Math.round((installmentLeft + lateFeesOwed) * 100) / 100;
  }, [repaymentDetails, installmentLeft, lateFeesOwed]);
  const amountPaidSoFar = parseFloat(repaymentDetails?.amount_paid || 0);
  const loanOutstanding = repaymentDetails?.loan_outstanding;

  // What the member typed; starts at what is left on the installment
  const [amountInput, setAmountInput] = useState("");
  useEffect(() => {
    if (repaymentDetails) setAmountInput(String(balance));
  }, [repaymentDetails, balance]);

  const amountError = repaymentDetails
    ? validatePaymentAmount({
        amount: amountInput,
        balance,
        outstanding: loanOutstanding,
        enabled: partialEnabled,
      })
    : "";

  const repaymentAmount = useMemo(() => {
    if (!repaymentDetails) return 0;
    if (!partialEnabled) return balance;
    const typed = Math.round(parseFloat(amountInput || 0) * 100) / 100;
    return Number.isFinite(typed) ? typed : 0;
  }, [repaymentDetails, partialEnabled, balance, amountInput]);

  const { data: feePreview } = useGetMemberPaymentFeePreviewQuery(
    {
      principal_amount: repaymentAmount,
      payment_channel: feePreviewChannel(paymentMode),
    },
    { skip: !repaymentAmount || repaymentAmount <= 0 || Boolean(amountError) }
  );

  const selectedBank = useMemo(
    () =>
      (companyBanks || []).find(
        (b) => String(b?.id) === String(selectedBankId)
      ),
    [companyBanks, selectedBankId]
  );

  // Get the due date from the specific repayment being paid
  // This should be the breakdown's due_at, not the loan's payback_at
  const repaymentDueDate = repaymentDetails?.due_at || null;

  const handlePayment = async () => {
    if (!repaymentDetails || !loanRequestId) {
      toast.error("Invalid repayment details");
      return;
    }

    if (amountError) {
      toast.error(amountError);
      return;
    }

    if (isSocietyBankMode(paymentMode)) {
      if (!selectedBankId) {
        toast.error("Please select the society account you paid into");
        return;
      }
      try {
        const payload = {
          loan_request_id: parseInt(loanRequestId, 10),
          repayment_id: repaymentDetails.id,
          company_bank_id: parseInt(selectedBankId, 10),
          member_note: memberNote?.trim() || undefined,
          ...(partialEnabled ? { amount: repaymentAmount } : {}),
        };
        const result = await payBankTransfer(payload).unwrap();
        if (!result?.status) {
          toast.error(result?.message || "Request failed");
          return;
        }
        toast.success(
          "Transfer request recorded. Pay the exact amount shown; staff will confirm after verifying your deposit."
        );
        navigate("/member/loan_management/loan_repayment");
      } catch (error) {
        toast.error(
          error?.data?.message ||
            error?.message ||
            "An error occurred. Please try again."
        );
      }
      return;
    }

    try {
      // The amount is only sent for part payments; the server re-checks it against what is owed
      const payload = {
        loan_request_id: parseInt(loanRequestId),
        repayment_id: repaymentDetails.id,
        ...(partialEnabled ? { amount: repaymentAmount } : {}),
        payment_provider: isPaystackMode(paymentMode) ? "paystack" : "alatpay",
        frontend_base_url: getFrontendBaseUrl(),
      };

      const response = await payRepayment(payload).unwrap();

      if (response?.data?.payment_url || response?.data?.authorization_url) {
        if (response?.data?.payment_reference) {
          sessionStorage.setItem(
            "pending_loan_repayment_reference",
            response.data.payment_reference
          );
        }
        toast.success("Redirecting to payment page...");
        if (!redirectToMemberOnlinePayment(response)) {
          toast.error("Payment URL not received. Please try again.");
        }
      } else {
        // If no payment URL, payment might have been processed immediately (tokenized card)
        if (response?.status) {
          toast.success(
            response?.message || "Loan repayment processed successfully"
          );
          // Navigate back to repayment page
          setTimeout(() => {
            navigate("/member/loan_management/loan_repayment");
          }, 1500);
        } else {
          toast.error(
            response?.message || "Failed to process payment. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          "An error occurred. Please try again."
      );
    }
  };

  const handleCancel = () => {
    navigate("/member/loan_management/loan_repayment");
  };

  if (isLoading) {
    return (
      <PageWrapper title="Pay Loan Repayment" back>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner animation="border" role="status" style={{ color: "var(--color-primary)" }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageWrapper>
    );
  }

  if (error || !repaymentDetails) {
    return (
      <PageWrapper title="Pay Loan Repayment" back>
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-4 text-center">
            <p className="text-danger mb-3">
              {error?.data?.message || "Repayment details not found"}
            </p>
            <ButtonComp
              btnText="Go Back"
              btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
              onClick={handleCancel}
            />
          </div>
        </CardComp>
      </PageWrapper>
    );
  }

  // Use the repayment's due_at (specific repayment installment due date)
  // NOT the loan's payback_at (total loan payback date)
  const dueDate = repaymentDueDate
    ? moment(repaymentDueDate).format("DD-MMM-YYYY")
    : "N/A";

  const isOverdue = repaymentDueDate
    ? moment(repaymentDueDate).isBefore(moment(), "day")
    : false;

  return (
    <PageWrapper title="Pay Loan Repayment" back>
      <section className="mt-6">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10">
            <MemberPendingBankTransfersCard flowFilter="loan" />
            <CardComp cardClassName="border-0 shadow-sm">
              <div className="p-4">
                <h4 className="fw-bold mb-4 text-primary1 text-center">
                  Pay Loan Repayment
                </h4>

                <CardComp cardClassName="border-0 mb-4" style={{ backgroundColor: "#f7f7f7" }}>
                  <div className="p-4">
                    <h6 className="fw-semibold text-black1 mb-3">Repayment Details</h6>
                    <div className="row g-3">
                      <div className="col-6">
                        <small className="text-muted d-block mb-1">
                          {partialEnabled ? "Installment" : "Amount"}
                        </small>
                        <span className="fw-bold text-primary1" style={{ fontSize: "20px" }}>
                          {formatMoney(
                            partialEnabled
                              ? parseFloat(repaymentDetails.amount || 0)
                              : repaymentAmount
                          )}
                        </span>
                        {lateFeesOwed > 0 && (
                          <div className="small text-danger mt-1">
                            Includes {formatMoney(lateFeesOwed)} late fees, which are paid first
                          </div>
                        )}
                        {partialEnabled && (
                          <div className="small text-muted mt-1">
                            Paid so far {formatMoney(amountPaidSoFar)} · Balance {formatMoney(installmentLeft)}
                          </div>
                        )}
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block mb-1">Due Date</small>
                        <span
                          className={`fw-bold ${
                            isOverdue ? "text-danger" : "text-black1"
                          }`}
                          style={{ fontSize: "20px" }}
                        >
                          {dueDate}
                        </span>
                        {isOverdue && (
                          <div className="mt-1">
                            <span className="badge bg-danger text-white small">
                              Overdue
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {partialEnabled && (
                      <div className="mt-3 pt-3 border-top">
                        <label className="fw-semibold mb-2 d-block" htmlFor="loan-repayment-amount">
                          Amount you want to pay
                        </label>
                        <CurrencyInput
                          id="loan-repayment-amount"
                          className={`w-100 form-control py-2 ${amountError ? "is-invalid" : ""}`}
                          prefix="₦"
                          decimalsLimit={2}
                          placeholder="Enter amount"
                          value={amountInput}
                          onValueChange={(value) => setAmountInput(value ?? "")}
                        />
                        {amountError ? (
                          <small className="text-danger d-block mt-1">{amountError}</small>
                        ) : (
                          <small className="text-muted d-block mt-1">
                            {paymentEffect({
                              amount: amountInput,
                              balance,
                              enabled: partialEnabled,
                            }) || "You can pay any amount from ₦100"}
                          </small>
                        )}
                        {loanOutstanding != null && (
                          <small className="text-muted d-block">
                            Total still owed on this loan: {formatMoney(parseFloat(loanOutstanding))}
                          </small>
                        )}
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex justify-content-between mb-2">
                        <small className="text-muted">Transaction charges</small>
                        <small className="fw-semibold">
                          {isSocietyBankMode(paymentMode) || amountError
                            ? formatMoney(0)
                            : formatMoney(getMemberTransactionChargesTotal(feePreview))}
                        </small>
                      </div>
                      <div className="d-flex justify-content-between pt-2 border-top">
                        <span className="fw-semibold">
                          {isSocietyBankMode(paymentMode)
                            ? "Exact amount to transfer"
                            : "Total to Pay"}
                        </span>
                        <span className="fw-bold text-primary1">
                          {formatMoney(
                            isSocietyBankMode(paymentMode) || amountError
                              ? repaymentAmount
                              : feePreview?.member_payable_total || repaymentAmount
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardComp>

                <div className="mb-3">
                  <MemberPaymentModeChooser
                    paymentMode={paymentMode}
                    onPaymentModeChange={handlePaymentModeChange}
                    companyBanks={companyBanks}
                    selectedBankId={selectedBankId}
                    onSelectedBankIdChange={setSelectedBankId}
                    memberNote={memberNote}
                    onMemberNoteChange={setMemberNote}
                    compact
                  >
                    <div
                      className="p-3 rounded mb-4"
                      style={{ backgroundColor: "#e1ecfe" }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <BsInfoCircle
                          size={20}
                          style={{ color: "var(--color-secondary)", marginTop: "2px", flexShrink: 0 }}
                        />
                        <div>
                          <p className="mb-1 small fw-semibold text-black1">
                            Online payment
                          </p>
                          <p className="mb-0 small text-muted">
                            You will be redirected to a secure checkout to complete
                            the payment. Your payment will be processed immediately
                            upon confirmation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </MemberPaymentModeChooser>
                  {isSocietyBankMode(paymentMode) && selectedBank && (
                    <div className="p-2 mb-3 rounded small" style={{ backgroundColor: "#f7f7f7" }}>
                      <p className="mb-1 fw-semibold">Pay into</p>
                      <p className="mb-0">{selectedBank.bank_name}</p>
                      <p className="mb-0">{selectedBank.account_name}</p>
                      <p className="mb-0 fw-semibold">{selectedBank.account_number}</p>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between gap-3">
                  <ButtonComp
                    btnText="Cancel"
                    btnClassName="border border-primary1 text-primary1 px-4 py-2 rounded bg-white"
                    onClick={handleCancel}
                    disabled={isPaying || isBankSubmitting}
                  />
                  <ButtonComp
                    btnText={
                      isSocietyBankMode(paymentMode)
                        ? isBankSubmitting
                          ? "Submitting..."
                          : "Submit transfer request"
                        : isPaying
                        ? "Processing..."
                        : "Pay Now"
                    }
                    btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
                    onClick={handlePayment}
                    loading={isSocietyBankMode(paymentMode) ? isBankSubmitting : isPaying}
                    disabled={
                      isPaying ||
                      isBankSubmitting ||
                      Boolean(amountError) ||
                      (isSocietyBankMode(paymentMode) && !selectedBankId)
                    }
                  />
                </div>
              </div>
            </CardComp>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}

